import { useCallback, useRef, useState, useEffect } from 'react';
import { resolveNode } from '../utils/responsive.js';

/**
 * Unified variant management hook
 * Single source of truth for adding/removing variants with undo/redo support
 */
export function useVariantManager({
    variantPopup,
    activeNodes,
    effectiveBreakpoint,
    nodeOps,
    updateNodes
}) {
    const isPreviewingRef = useRef(false);
    const originalVariantRef = useRef(null);

    // Reset refs when popup target changes to prevent stale state
    useEffect(() => {
        originalVariantRef.current = null;
        isPreviewingRef.current = false;
    }, [variantPopup?.node?.id]);

    // Derive appliedVariant directly from props/refs to avoid useEffect flicker
    const appliedVariant = (() => {
        if (!variantPopup?.node) return '';

        // If previewing, use the original variant to prevent list jumping
        if (isPreviewingRef.current && originalVariantRef.current !== null) {
            return originalVariantRef.current;
        }

        const currentNode = activeNodes.find(n => n.id === variantPopup.node.id);
        if (currentNode) {
            const resolvedNode = resolveNode(currentNode, effectiveBreakpoint);
            return resolvedNode.props.variant || '';
        }
        return '';
    })();

    /**
     * Set variant (creates history entry for undo/redo)
     * This is the ONLY way to permanently change variants
     */
    const setVariant = useCallback((newVariantString) => {
        if (!variantPopup?.node) return;

        const rawNode = variantPopup.node;
        const updates = { props: { variant: newVariantString } };

        // Clear responsive override if it exists
        if (effectiveBreakpoint && effectiveBreakpoint !== 'default') {
            const currentOverrides = rawNode.props.responsiveOverrides?.[effectiveBreakpoint] || {};
            if ('variant' in currentOverrides) {
                const { variant: _, ...cleanedOverrides } = currentOverrides;
                const allOverrides = rawNode.props.responsiveOverrides || {};
                updates.props.responsiveOverrides = {
                    ...allOverrides,
                    [effectiveBreakpoint]: cleanedOverrides
                };
            }
        }

        // Create history entry and update nodes
        isPreviewingRef.current = false;
        nodeOps.handlePropertyUpdate(updates, [rawNode.id]);
        originalVariantRef.current = newVariantString || '';
        // appliedVariant is derived from props, so it updates automatically
    }, [variantPopup, nodeOps, effectiveBreakpoint]);

    /**
     * Add a variant to the current list
     */
    const addVariant = useCallback((key) => {
        const currentVariants = appliedVariant.split(' ').filter(Boolean);
        if (!currentVariants.includes(key)) {
            currentVariants.push(key);
        }
        const newVariantString = currentVariants.join(' ');
        setVariant(newVariantString);
    }, [appliedVariant, setVariant]);

    /**
     * Remove a variant from the current list
     */
    const removeVariant = useCallback((key) => {
        const currentVariants = appliedVariant.split(' ').filter(Boolean);
        const filtered = currentVariants.filter(v => v !== key);
        const newVariantString = filtered.length > 0 ? filtered.join(' ') : undefined;
        setVariant(newVariantString);
    }, [appliedVariant, setVariant]);

    /**
     * Preview a variant temporarily (no history entry)
     */
    const previewVariant = useCallback((key) => {
        if (!variantPopup?.node) return;

        const rawNode = variantPopup.node;
        const node = resolveNode(rawNode, effectiveBreakpoint);

        // Store original on first preview
        if (originalVariantRef.current === null) {
            originalVariantRef.current = node.props.variant || '';
        }

        // Build preview variant string
        const currentVariants = appliedVariant.split(' ').filter(Boolean);
        if (!currentVariants.includes(key)) {
            currentVariants.push(key);
        }
        const newVariantString = currentVariants.join(' ');

        // Set preview flag to block sync
        isPreviewingRef.current = true;

        // Directly update nodes WITHOUT history
        updateNodes(activeNodes.map(n => {
            if (n.id !== rawNode.id) return n;

            const updatedNode = { ...n, props: { ...n.props, variant: newVariantString } };

            // Clear responsive override if exists
            if (effectiveBreakpoint && effectiveBreakpoint !== 'default') {
                const currentOverrides = n.props.responsiveOverrides?.[effectiveBreakpoint] || {};
                if ('variant' in currentOverrides) {
                    const { variant: _, ...cleanedOverrides } = currentOverrides;
                    const allOverrides = n.props.responsiveOverrides || {};
                    updatedNode.props.responsiveOverrides = {
                        ...allOverrides,
                        [effectiveBreakpoint]: cleanedOverrides
                    };
                }
            }

            return updatedNode;
        }));
    }, [variantPopup, effectiveBreakpoint, appliedVariant, activeNodes, updateNodes]);

    /**
     * Restore from preview back to applied variant
     */
    const restoreFromPreview = useCallback(() => {
        if (!variantPopup?.node) return;

        const rawNode = variantPopup.node;
        isPreviewingRef.current = false;

        // Restore to appliedVariant (read from closure)
        updateNodes(activeNodes.map(n => {
            if (n.id !== rawNode.id) return n;

            const updatedNode = { ...n, props: { ...n.props, variant: appliedVariant } };

            // Clear responsive override if exists
            if (effectiveBreakpoint && effectiveBreakpoint !== 'default') {
                const currentOverrides = n.props.responsiveOverrides?.[effectiveBreakpoint] || {};
                if ('variant' in currentOverrides) {
                    const { variant: _, ...cleanedOverrides } = currentOverrides;
                    const allOverrides = n.props.responsiveOverrides || {};
                    updatedNode.props.responsiveOverrides = {
                        ...allOverrides,
                        [effectiveBreakpoint]: cleanedOverrides
                    };
                }
            }

            return updatedNode;
        }));
    }, [variantPopup, effectiveBreakpoint, appliedVariant, activeNodes, updateNodes]);

    return {
        appliedVariant,
        setVariant,
        addVariant,
        removeVariant,
        previewVariant,
        restoreFromPreview,
        originalVariantRef
    };
}
